import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Box, Button, Stack, Typography } from '@mui/material';

import {
  getFilterOrganizationsQueryKey,
  getGetOrganizationBySlugQueryKey,
  OrganizationRequestDTO,
  PlatformLocale,
  useCreateDraftOrganization,
  useGetOrganizationBySlug,
  useOnboardOrganization,
  useUpdateOrganization
} from '@/api/generated';
import OrganizationBigIcon from '@/assets/icons/organization-big.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import HorizontalStepper from '@/components/HorizontalStepper';
import LoadingButton from '@/components/LoadingButton';
import { useSnackbar } from '@/hooks/useSnackbar';
import { borders } from '@/theme/constants';
import SettingsModalForm, { SettingsModalFormRef } from '@/views/SettingsModalForm/SettingsModalForm';

import BasicInfoTab from './BasicInfoTab';
import { DEFAULT_VALUES } from './defaults';
import MoreInfoTab from './MoreInfoTab';
import OrgAdminTab from './OrgAdminTab';
import {
  getCreateEnterpriseOrgBasicTabSchema,
  getCreateOrgAdminsTabSchema,
  getCreateOrgBasicTabSchema,
  getCreateOrgGlobalSchema,
  getCreateOrgMoreInfoTabSchema
} from './schema';
import { CreateOrganizationFormData } from './types';

interface CreateOrganizationModalProps {
  isOpen?: boolean;
  onClose: () => void;
  currentFilter?: OrganizationRequestDTO | null;
  draftOrganizationSlug: string | null;
}

interface ValidationError {
  path: string;
  type: string;
  message: string;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen = true,
  onClose,
  currentFilter,
  draftOrganizationSlug
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation(['organizations', 'common', 'validation']);
  const { showMessage } = useSnackbar();
  const { mutateAsync: createOrganization, isLoading: isCreateLoading } = useCreateDraftOrganization();
  const { mutateAsync: onboardOrganization, isLoading: isOnboardLoading } = useOnboardOrganization();
  const { mutateAsync: updateOrganization } = useUpdateOrganization();
  const formRef = useRef<SettingsModalFormRef<FieldValues>>(null);
  // TODO: ensure isFull provides proper and full data
  const { data: draftOrganization } = useGetOrganizationBySlug(
    draftOrganizationSlug!,
    { isFull: true },
    {
      query: { enabled: !!draftOrganizationSlug }
    }
  );

  const handleSave = useCallback(
    async (shouldOnboard: boolean) => {
      try {
        setIsSaving(true);
        const values = formRef?.current?.getValues();
        const data = values as CreateOrganizationFormData;
        const organizationData = {
          name: data.name,
          legalName: data.legalName,
          offices: Array.isArray(data.offices) ? data.offices : [data.offices].filter(Boolean),
          countries: Array.isArray(data.countries) ? data.countries : [data.countries].filter(Boolean),
          isEnterprise: data.isEnterprise,
          keyAccountManagers: data.keyAccountManagers,
          locales: data.locales as PlatformLocale[],
          jiraId: data.jiraId,
          legacyId: data.legacyId,
          pipedriveId: data.pipedriveId,
          admins: [{ email: data.admin.email, phone: data.admin.phone }]
        };

        let newOrganizationSlug: string | undefined;
        if (draftOrganizationSlug) {
          await updateOrganization({
            organizationSlug: draftOrganizationSlug!,
            data: organizationData
          });
          newOrganizationSlug = draftOrganizationSlug;
        } else {
          const result = await createOrganization({
            data: organizationData
          });
          newOrganizationSlug = result?.slug;
        }

        if (shouldOnboard) {
          await onboardOrganization({
            slug: newOrganizationSlug!
          });
        }

        if (currentFilter) {
          await queryClient.invalidateQueries(getFilterOrganizationsQueryKey(currentFilter));
        }
        if (draftOrganizationSlug) {
          await queryClient.invalidateQueries(
            getGetOrganizationBySlugQueryKey(draftOrganizationSlug!, { isFull: true })
          );
          await queryClient.invalidateQueries(
            getGetOrganizationBySlugQueryKey(draftOrganizationSlug!, { isFull: false })
          );
        }
      } finally {
        setIsSaving(false);
      }
    },
    [createOrganization, currentFilter, onboardOrganization, queryClient, draftOrganizationSlug, updateOrganization]
  );

  const handleSaveDraft = useCallback(async () => {
    try {
      await handleSave(false);
      showMessage(t('layouts.organizations.create.success'));
      onClose();
    } catch (error) {
      showMessage(t('layouts.organizations.create.error'));
    }
  }, [handleSave, onClose, showMessage, t]);

  const handleOnboard = useCallback(async () => {
    try {
      await handleSave(true);
      showMessage(t('layouts.organizations.create.success'));
    } catch (error) {
      showMessage(t('layouts.organizations.create.error'));
    }
  }, [handleSave, showMessage, t]);

  const defaultValues = useMemo(() => {
    if (draftOrganizationSlug && draftOrganization) {
      const isEnterprise = draftOrganization.isEnterprise;

      return {
        name: draftOrganization.name,
        legalName: draftOrganization.legalName,
        offices: isEnterprise ? draftOrganization.offices : draftOrganization?.offices?.[0],
        countries: isEnterprise ? draftOrganization.countries : draftOrganization?.countries?.[0],
        isEnterprise: draftOrganization.isEnterprise,
        keyAccountManagers: draftOrganization.keyAccountManagers?.filter(user => user?.id)?.map(user => user.id!),
        locales: draftOrganization?.locales,
        jiraId: draftOrganization.jiraId,
        legacyId: draftOrganization.legacyId,
        pipedriveId: draftOrganization.pipedriveId,
        admin: {
          email: draftOrganization.admins?.[0]?.email || '',
          phone: draftOrganization.admins?.[0]?.phone || ''
        }
      };
    } else if (!draftOrganizationSlug) {
      return DEFAULT_VALUES;
    }

    return null;
  }, [draftOrganizationSlug, draftOrganization]);

  const [isEnterprise, setIsEnterprise] = useState(false);

  useEffect(() => {
    const watch = formRef.current?.watch;

    const subscription = watch?.((values: FieldValues) => {
      const data = values as CreateOrganizationFormData;
      setIsEnterprise(data.isEnterprise);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const schema = useMemo(() => getCreateOrgGlobalSchema(t, isEnterprise), [t, isEnterprise]);

  if (!defaultValues) {
    return <BackdropLoader />;
  }

  return (
    <SettingsModalForm
      ref={formRef}
      isOpen={isOpen}
      onClose={onClose}
      defaultValues={defaultValues}
      onSave={handleOnboard}
      isSaving={isCreateLoading || isOnboardLoading || isSaving}
      validation={schema}
      head={
        <Stack
          direction="row"
          alignItems="center"
          gap={2.5}
          sx={{
            px: 0.5,
            pt: 2.5,
            pb: 2
          }}
        >
          <OrganizationBigIcon />
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            {t('layouts.organizations.create.title')}
          </Typography>
        </Stack>
      }
      sx={{
        '&.CreateOrganizationModal-root .SettingsModalForm-root': {
          maxWidth: 600,
          minHeight: 'auto',
          height: 'auto'
        },
        '&.CreateOrganizationModal-root .MuiDialogTitle-root': {
          py: 0,
          borderBottom: borders.default,
          borderColor: 'grey.300'
        }
      }}
      className="CreateOrganizationModal-root"
    >
      <CreateOrganizationModalContent
        handleSaveDraft={handleSaveDraft}
        isLoading={isCreateLoading || isOnboardLoading || isSaving}
      />
    </SettingsModalForm>
  );
};

interface CreateOrganizationModalContentProps {
  handleSaveDraft: () => void;
  isLoading?: boolean;
}

const CreateOrganizationModalContent: FC<CreateOrganizationModalContentProps> = ({ handleSaveDraft, isLoading }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { t } = useTranslation(['organizations', 'common', 'validation']);
  const [currentTabErrors, setCurrentTabErrors] = useState<ValidationError[] | null>(null);
  const { watch, getValues } = useFormContext<CreateOrganizationFormData>();
  const isEnterprise = watch('isEnterprise');
  const organizationName = watch('name');

  const handleNext = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (currentStep < 2) {
        e.preventDefault();
        setCurrentStep(prev => prev + 1);
      }
    },
    [currentStep]
  );

  const handleBack = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const renderStepContent = useCallback(() => {
    switch (currentStep) {
      case 0:
        return <BasicInfoTab />;
      case 1:
        return <MoreInfoTab />;
      case 2:
        return <OrgAdminTab />;
      default:
        return null;
    }
  }, [currentStep]);

  const currentTabSchema = useMemo(() => {
    switch (currentStep) {
      case 0:
        return isEnterprise ? getCreateEnterpriseOrgBasicTabSchema(t) : getCreateOrgBasicTabSchema(t);
      case 1:
        return getCreateOrgMoreInfoTabSchema(t);
      case 2:
        return getCreateOrgAdminsTabSchema(t);
      default:
        return null;
    }
  }, [currentStep, isEnterprise, t]);

  useEffect(() => {
    const validate = async (values: FieldValues) => {
      try {
        await currentTabSchema?.validate(values, {
          abortEarly: false
        });

        setCurrentTabErrors(null);
      } catch (errors) {
        const errorList = (errors as { inner: { path: string; type: string; message: string }[] })?.inner;
        setCurrentTabErrors(errorList);
      }
    };

    const values = getValues();
    const subscription = watch(validate);

    if (values) {
      validate(values);
    }

    return () => subscription?.unsubscribe?.();
  }, [currentTabSchema, watch, getValues]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ m: 4, mb: 2 }}>
        <HorizontalStepper
          labels={[
            t('layouts.organizations.create.tabs.basicInfo'),
            t('layouts.organizations.create.tabs.moreInfo'),
            t('layouts.organizations.create.tabs.orgAdmin')
          ]}
          value={currentStep}
        />
      </Box>

      {renderStepContent()}

      <Stack
        direction="row"
        justifyContent="space-between"
        width="100%"
        sx={{
          paddingInlineStart: 2
        }}
      >
        {currentStep > 0 && (
          <Button onClick={handleBack} variant="text">
            {t('common:actions.back')}
          </Button>
        )}

        <Stack direction="row" gap={2} p={3} justifyContent="end" flexGrow={1}>
          <Button onClick={handleSaveDraft} variant="text" disabled={!organizationName}>
            {t('layouts.organizations.create.saveDraft')}
          </Button>
          <LoadingButton
            isLoading={isLoading}
            onClick={handleNext}
            variant="contained"
            disabled={isLoading || !!currentTabErrors}
            type={currentStep === 2 ? 'submit' : 'button'}
          >
            {currentStep === 2 ? t('layouts.organizations.create.submit') : t('common:actions.next')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CreateOrganizationModal;
